import * as analytics from "@/modules/analytics";
import type { ExperienceId } from "@/types/experience";
import { experienceStore } from "@/stores/experience/store";
import type { ExperienceModel } from "@/stores/experience/types";

interface RegionPayload {
  region: string;
}

interface IndexPayload extends RegionPayload {
  index: number;
}

interface IdPayload extends RegionPayload {
  id: ExperienceId;
}

interface IdsPayload extends RegionPayload {
  ids: ExperienceId[];
}

interface AddFavoritePayload extends RegionPayload {
  cname: number;
  name: string;
  index: number;
  /** Never set: the payload carries `cname`, so the article lookup gets no cluster index. */
  cindex?: number;
}

export function addFavorite(
  region: string,
  name: string,
  cname: number,
  index: number,
) {
  return experienceStore.dispatch(
    { region: region, cname: cname, name: name, index: index },
    (state, data: AddFavoritePayload) => {
      const article = state.experienceData.getArticle(
        data.name,
        data.cindex,
        data.index,
      );
      if (!article) {
        return false;
      }
      state.favoritesData.addFavorite(article.id);
      analytics.track("Add Favorite", {
        pix_offset: state.experienceData.nowTime,
        pix_appregion: data.region,
        pix_id: article.id,
        pix_cindex: data.cindex,
        pix_index: data.index,
      });
    },
  );
}

export function showFavorites(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      state.applicationData.setShowFavorites(true);
      state.applicationData.setShowShopWindow(false);
      state.template.setShowPanel(false);
      state.applicationData.setPauseVideo(true);

      analytics.track("Show Favorites", {
        pix_offset: state.experienceData.nowTime,
        pix_appregion: data.region,
      });
    },
  );
}

export function dismissFavorites(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      state.applicationData.setShowFavorites(false);
      state.applicationData.setShowShopWindow(true);
      state.template.setShowPanel(true);
      analytics.track("Dismiss Favorites", {
        pix_appregion: data.region,
      });
    },
  );
}

function trackSelectFavorite(
  state: ExperienceModel,
  how: string,
  region: string,
) {
  analytics.track("Select Favorite", {
    pix_how: how,
    pix_appregion: region,
    pix_id: state.favoritesData.getFavorite(),
    pix_favorite_index: state.favoritesData.favoriteIndex,
  });
}

function trackFavoriteImage(
  state: ExperienceModel,
  name: string,
  region: string,
) {
  analytics.track(name, {
    pix_appregion: region,
    pix_id: state.favoritesData.getFavorite(),
    pix_favorite_index: state.favoritesData.favoriteIndex,
    pix_favorite_image: state.favoritesData.imageIndex,
  });
}

export function incrementFavoriteIndex(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.incrementFavoriteIndex()) {
        return false;
      }
      trackSelectFavorite(state, "increment", data.region);
    },
  );
}

export function decrementFavoriteIndex(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.decrementFavoriteIndex()) {
        return false;
      }
      trackSelectFavorite(state, "decrement", data.region);
    },
  );
}

export function selectFavorite(region: string, index: number) {
  return experienceStore.dispatch(
    { region: region, index: index },
    (state, data: IndexPayload) => {
      const oldIndex = state.favoritesData.favoriteIndex;
      if (!state.favoritesData.selectFavorite(data.index)) {
        return false;
      }
      trackSelectFavorite(
        state,
        data.index > oldIndex ? "increment" : "decrement",
        data.region,
      );
    },
  );
}

export function selectFavoriteImage(region: string, index: number) {
  return experienceStore.dispatch(
    { region: region, index: index },
    (state, data: IndexPayload) => {
      if (!state.favoritesData.selectFavoriteImage(data.index)) {
        return false;
      }
      trackFavoriteImage(state, "Select Favorite Image", data.region);
    },
  );
}

export function incrementFavoriteImageIndex(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.incrementFavoriteImageIndex()) {
        return false;
      }
      trackFavoriteImage(state, "Increment Favorite Image", data.region);
    },
  );
}

export function decrementFavoriteImageIndex(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.decrementFavoriteImageIndex()) {
        return false;
      }
      trackFavoriteImage(state, "Decrement Favorite Image", data.region);
    },
  );
}

export function addArticleToFavorites(region: string, id: ExperienceId) {
  return experienceStore.dispatch(
    { region: region, id: id },
    (state, data: IdPayload) => {
      if (!state.favoritesData.addFavorite(data.id)) {
        return false;
      }

      analytics.track("Add Favorite", {
        pix_appregion: data.region,
        pix_id: data.id,
      });
    },
  );
}

/** `ids` are the article ids the favourites UI passes through unchecked. */
export function addArticlesToFavorites(region: string, ids: unknown[]) {
  return experienceStore.dispatch(
    { region: region, ids: ids as ExperienceId[] },
    (state, data: IdsPayload) => {
      if (!state.favoritesData.addFavorites(data.ids)) {
        return false;
      }

      for (const id of data.ids) {
        analytics.track("Add Favorite", {
          pix_appregion: data.region,
          pix_id: id,
        });
      }
    },
  );
}

export function removeArticleFromFavorites(region: string, id: ExperienceId) {
  return experienceStore.dispatch(
    { region: region, id: id },
    (state, data: IdPayload) => {
      if (!state.favoritesData.removeFavorite(data.id)) {
        return false;
      }

      analytics.track("Remove Favorite", {
        pix_appregion: data.region,
        pix_id: data.id,
      });
    },
  );
}

/** `ids` are the article ids the favourites UI passes through unchecked. */
export function removeArticlesFromFavorites(region: string, ids: unknown[]) {
  return experienceStore.dispatch(
    { region: region, ids: ids as ExperienceId[] },
    (state, data: IdsPayload) => {
      if (!state.favoritesData.removeFavorites(data.ids)) {
        return false;
      }

      for (const id of data.ids) {
        analytics.track("Remove Favorite", {
          pix_appregion: data.region,
          pix_id: id,
        });
      }
    },
  );
}

export function addAllArticlesToFavorites(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.addAll()) {
        return false;
      }

      analytics.track("Add All Favorites", {
        pix_appregion: data.region,
      });
    },
  );
}

export function removeAllArticlesFromFavorites(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (!state.favoritesData.removeAll()) {
        return false;
      }

      analytics.track("Remove All Favorites", {
        pix_appregion: data.region,
      });
    },
  );
}

export function removeFavoriteAtIndex(region: string, index: number) {
  return experienceStore.dispatch(
    { region: region, index: index },
    (state, data: IndexPayload) => {
      const favorite = state.favoritesData.getFavorite();
      const favoriteIndex = data.index;

      if (!state.favoritesData.removeFavoriteAtIndex(data.index)) {
        return false;
      }

      if (state.favoritesData.getFavoritesCount() === 0) {
        state.applicationData.setShowFavorites(false);
        state.applicationData.setShowShopWindow(true);
        state.template.setShowPanel(true);
      }

      analytics.track("Remove Favorite", {
        pix_appregion: data.region,
        pix_id: favorite,
        pix_favorite_index: favoriteIndex,
      });
    },
  );
}
